import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useBackendLabel } from "@/api/backends";
import { useRun, useStopRun, useRebaseRun } from "@/api/runs";
import { useRunStream, type NoiseProgressMap } from "@/hooks/useRunStream";
import { usePermissions } from "@/hooks/usePermissions";
import { RunStatusBadge } from "./RunStatusBadge";
import { RunPhaseStepper } from "./RunPhaseStepper";
import { NoiseStreamPanel } from "./NoiseStreamPanel";
import { RunAlertsPanel } from "./RunAlertsPanel";
import { RunBackendsPanel } from "./RunBackendsPanel";
import { VerifyQueriesPanel } from "./VerifyQueriesPanel";

export function RunDetailPage() {
  const { runId } = useParams();
  const run = useRun(runId);
  const stop = useStopRun();
  const rebase = useRebaseRun();
  const backendLabel = useBackendLabel();
  const [noiseProgress, setNoiseProgress] = useState<NoiseProgressMap>({});
  const permissions = usePermissions();
  useRunStream(runId, setNoiseProgress);

  if (!run.data) return <div className="p-6">Loading…</div>;
  const r = run.data;
  const backend = backendLabel(r.backend_id || "");

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <Link to="/runs" className="text-sm underline">← Runs</Link>
        <h2 className="text-2xl font-semibold">{r.id}</h2>
        <RunStatusBadge status={r.status} />
      </div>

      <Card className="p-4 space-y-3">
        <div className="text-sm text-muted-foreground">
          Scenario <span className="font-mono text-foreground">{r.scenario_id}</span> →
          Backend{" "}
          <span className="text-foreground" title={r.backend_id ?? ""}>
            {backend.label}
            {backend.kind && (
              <span className="ml-1 text-[10px] uppercase tracking-wide text-muted-foreground/70">
                {backend.kind}
              </span>
            )}
          </span>
        </div>
        <RunPhaseStepper phase={r.phase} />
        {r.phase === "pushing_history" && r.progress && Object.keys(r.progress).length > 0 && (
          <ProgressBars progress={r.progress} />
        )}
        <div className="flex gap-2">
          <Button
            variant="outline"
            disabled={!permissions.canMutate || r.status !== "idle" || rebase.isPending}
            onClick={() => runId && rebase.mutate(runId)}
          >
            {rebase.isPending ? "Rebasing…" : "Rebase"}
          </Button>
          <Button
            variant="outline"
            disabled={
              !permissions.canMutate
              || r.status === "stopped"
              || r.status === "failed"
              || stop.isPending
            }
            onClick={() => runId && stop.mutate(runId)}
          >
            {stop.isPending ? "Stopping…" : "Stop"}
          </Button>
        </div>
        {!permissions.canMutate && (
          <p className="text-xs text-muted-foreground">
            {permissions.mutationReason ?? "Insufficient permissions for this action."}
          </p>
        )}
      </Card>

      {r.error && (
        <Alert variant="destructive">
          <AlertTitle>
            {r.error.code === "alert_burst_rejected"
              ? "Alert POSTs rejected by Raven"
              : r.error.code}
          </AlertTitle>
          <AlertDescription>
            {r.error.message}
            {r.error.code === "alert_burst_rejected" && (
              <div className="mt-1 text-xs">
                {/* Point at the field that actually failed. Raven returns 401
                    for both a stale ingest_token and an HMAC mismatch, so a
                    generic "check both" hint sends operators down the wrong
                    path half the time. */}
                {/invalid ingest token/i.test(
                  String(r.error.details?.first_error_snippet ?? ""),
                ) ? (
                  <>
                    Raven did not recognize the token. Repaste{" "}
                    <code>raven_ingest_token</code> in{" "}
                    <a
                      href={`/backends/${r.backend_id}`}
                      className="underline underline-offset-2"
                    >
                      Backends
                    </a>
                    . Note that editing a webhook connection in the NeuBird UI
                    clears its ingest token — if someone saved that connection,
                    the token must be reissued.
                  </>
                ) : /invalid hmac/i.test(
                    String(r.error.details?.first_error_snippet ?? ""),
                  ) ? (
                  <>
                    The token was accepted but the signature did not match. Set{" "}
                    <code>raven_hmac_secret</code> in{" "}
                    <a
                      href={`/backends/${r.backend_id}`}
                      className="underline underline-offset-2"
                    >
                      Backends
                    </a>{" "}
                    to the connection's HMAC secret (blank falls back to{" "}
                    <code>demosim</code>).
                  </>
                ) : (
                  <>
                    Check the backend's <code>raven_ingest_token</code> /{" "}
                    <code>raven_hmac_secret</code> in{" "}
                    <a
                      href={`/backends/${r.backend_id}`}
                      className="underline underline-offset-2"
                    >
                      Backends
                    </a>
                    .
                  </>
                )}
              </div>
            )}
          </AlertDescription>
        </Alert>
      )}

      {r.last_push_result && (
        <Card className="p-4">
          <h3 className="font-medium mb-2">Last push result</h3>
          <pre className="text-xs bg-muted p-2 rounded overflow-auto">
            {JSON.stringify(r.last_push_result, null, 2)}
          </pre>
        </Card>
      )}

      {r.backends && r.backends.length > 0 && (
        <RunBackendsPanel backends={r.backends} />
      )}

      <RunAlertsPanel
        runId={r.id}
        deliveries={
          r.alert_burst_result
            ? Object.fromEntries(
                r.alert_burst_result.deliveries.map((d) => [d.external_id, d]),
              )
            : undefined
        }
      />

      <NoiseStreamPanel
        bindings={r.template_bindings}
        progress={noiseProgress}
        noiseDeliveryStats={r.noise_delivery_stats}
      />

      <VerifyQueriesPanel runId={r.id} />
    </div>
  );
}

interface ProgressLeaf {
  pushed?: number;
  total?: number;
}

// Progress dict comes in two shapes:
// - Flat (single-backend / legacy): { signal: {pushed, total} }
// - Nested (multi-backend):         { backend_id: { signal: {pushed, total} } }
// Detect by checking if a value has `pushed` on it — the flat leaf — vs
// containing another object.
function ProgressBars({ progress }: { progress: Record<string, unknown> }) {
  const rows: { label: string; pushed: number; total: number }[] = [];
  for (const [key, value] of Object.entries(progress)) {
    if (value && typeof value === "object" && "pushed" in (value as ProgressLeaf)) {
      const leaf = value as ProgressLeaf;
      rows.push({
        label: key,
        pushed: Number(leaf.pushed ?? 0),
        total: Number(leaf.total ?? 0),
      });
    } else if (value && typeof value === "object") {
      // Nested: `key` is a backend_id; iterate its signals.
      for (const [signal, leafValue] of Object.entries(value as Record<string, ProgressLeaf>)) {
        rows.push({
          label: `${key} · ${signal}`,
          pushed: Number(leafValue.pushed ?? 0),
          total: Number(leafValue.total ?? 0),
        });
      }
    }
  }
  return (
    <div className="space-y-1.5">
      {rows.map((row) => {
        const pct = row.total > 0 ? Math.round((row.pushed / row.total) * 100) : 0;
        return (
          <div key={row.label} className="text-xs">
            <div className="flex items-baseline justify-between">
              <span className="font-mono text-foreground">{row.label}</span>
              <span className="text-muted-foreground tabular-nums">
                {row.pushed.toLocaleString()} / {row.total.toLocaleString()} ({pct}%)
              </span>
            </div>
            <div className="h-1 bg-muted rounded overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
